import { arrayMove } from '@dnd-kit/sortable';
import type { LayoutType } from 'types';
import type { TemplateFormsState } from '../index';

interface Payload {
  sectionName: string;
  fromSubTabId: string | null;
  toSubTabId: string | null;
  layoutType: LayoutType;
}

export const reorderSubTab = (state: TemplateFormsState, { payload }: { payload: Payload }) => {
  const { sectionName, fromSubTabId, toSubTabId, layoutType } = payload;
  const sections = state[layoutType].sections;

  const sectionIndex = sections.findIndex((section) => section.name === sectionName);
  if (sectionIndex === -1) return;

  const section = sections[sectionIndex];

  if (fromSubTabId === toSubTabId) return;

  // The default (null) sub-tab is not in subTabs[] and always renders first.
  // To allow true reordering involving the default tab, we convert it to a real
  // entity when a drag involves it, then reorder within subTabs[].
  // If subTabs is null but we need to reorder (e.g. default tab drag), initialize it.

  const ensureRealTab = (id: string | null): string => {
    if (id !== null) return id;
    // Convert the default (null) sub-tab into a real entity inserted at the front,
    // since the default tab always renders first.
    const newId = crypto.randomUUID();
    const existingSubTabs = section.subTabs ?? [];
    section.subTabs = [{ id: newId, order: 0, name: '', translations: [] }, ...existingSubTabs.map((st) => ({ ...st, order: st.order + 1 }))];
    // Reassign all items from null to the new entity
    section.items = section.items.map((item) => ((item.subTabId ?? null) === null ? { ...item, subTabId: newId } : item));
    return newId;
  };

  const realFromId = ensureRealTab(fromSubTabId);
  const realToId = ensureRealTab(toSubTabId);

  if (realFromId === realToId) return;

  const subTabs = section.subTabs ?? [];
  const fromIndex = subTabs.findIndex((st) => st.id === realFromId);
  const toIndex = subTabs.findIndex((st) => st.id === realToId);

  if (fromIndex === -1 || toIndex === -1) return;
  if (fromIndex === toIndex) return;

  const reordered = arrayMove(subTabs, fromIndex, toIndex);
  section.subTabs = reordered.map((st, index) => ({ ...st, order: index }));
};
